import {
  adSizeMappings,
  adSlotSizes,
  adTargetings,
} from "@config/AdsConstants";
import { generateAdvertElementId } from "@elements/Advertisement/helpers";
import {
  AdTargetingType,
  AdUnitProps,
  ArticleAdPageTargetingType,
  ArticleAdSlotTargetingType,
} from "@typings/Ads.d";
import { useCallback, useEffect, useState } from "react";

/**
 * This component renders an IMU ad.
 * @param rootClassName - The root class name.
 * @param type - The type of ad unit.
 * @param slot - The ad slot.
 * @param id - The id of the ad.
 * @see https://developers.google.com/doubleclick-gpt/reference
 * @returns A IMU ad component. If the script is not ready, it returns error. If the script is ready, it returns the ad.
 */
export const AdUnit = ({
  rootClassName = "",
  type,
  slot,
  id = generateAdvertElementId(type),
  pubAdTargeting,
  gsChannels,
  articlePageTarget,
  articleSlotTarget,
}: AdUnitProps) => {
  const [error, setError] = useState<string>("");
  const [adSlot, setAdSlot] =
    useState<ReturnType<typeof googletag.defineSlot>>();
  const adSlotSize = adSlotSizes[type] as googletag.GeneralSize | undefined;
  const adSizeMapping = adSizeMappings[type];
  const adTarget = adTargetings[type];

  /**
   * This function is called when the Google script is loaded.
   * It defines the ad slot and loads the ad.
   * @returns void
   * @see https://developers.google.com/doubleclick-gpt/reference#googletag.cmd.push
   */
  const handleDisplayAd = useCallback(() => {
    try {
      // Make sure that googletag.cmd exists.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unnecessary-condition
      window.googletag =
        (window.googletag as typeof googletag | undefined) || {};
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unnecessary-condition
      ((window.googletag as typeof googletag).cmd as googletag.CommandArray) =
        (window.googletag as typeof googletag).cmd || []; // eslint-disable-line @typescript-eslint/no-unnecessary-condition
      // Correct: Queueing the callback on the command queue.
      (
        (window.googletag as typeof googletag).cmd as googletag.CommandArray
      ).push(function () {
        const pageTargeting: Record<string, string | string[]> = {};
        pubAdTargeting.forEach((target: AdTargetingType) => {
          pageTargeting[target.key] = target.value;
        });

        articlePageTarget?.forEach((target: ArticleAdPageTargetingType) => {
          pageTargeting[target.key] = target.value;
        });

        if (Object.keys(pageTargeting).length > 0) {
          googletag.setConfig({ targeting: pageTargeting });
        }

        // Remove any existing ad slots with the same id.
        googletag
          .pubads()
          .getSlots()
          .forEach(function (_slot: googletag.Slot) {
            if (_slot.getSlotElementId()) {
              if (_slot.getSlotElementId() === id) {
                googletag.destroySlots([_slot]);
              }
            }
          });
        // Create the ad slot.
        if (undefined != adSlotSize) {
          const _adSlot = googletag.defineSlot(slot, adSlotSize, id) as
            googletag.Slot | undefined;

          if (undefined != _adSlot) {
            _adSlot.addService(googletag.pubads());

            if (adSizeMapping) _adSlot.defineSizeMapping(adSizeMapping);

            const slotTargeting: Record<string, string | string[]> = {};
            adTarget?.forEach((target: AdTargetingType) => {
              slotTargeting[target.key] = target.value;
            });

            // Set gs_channels targeting.
            if (gsChannels && gsChannels.length > 0) {
              slotTargeting.gs_channels = gsChannels.join(",");
            }

            if ("gsChannels_fr" in window) {
              slotTargeting.fr = String(window.gsChannels_fr === "true");
            }

            articleSlotTarget?.forEach((target: ArticleAdSlotTargetingType) => {
              slotTargeting[target.key] = target.value;
            });

            if (Object.keys(slotTargeting).length > 0) {
              _adSlot.setConfig({ targeting: slotTargeting });
            }

            googletag.setConfig({ singleRequest: true });
            googletag.enableServices();

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            window.googletag.slots[type] = _adSlot;

            // Store the ad slot for later use.
            setAdSlot(_adSlot);
          }
        }
      });
    } catch (_error: unknown) {
      setError("Please refresh");
    }
  }, [
    pubAdTargeting,
    articlePageTarget,
    adSlotSize,
    id,
    slot,
    adSizeMapping,
    adTarget,
    articleSlotTarget,
    type,
    gsChannels,
  ]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (slot && !adSlot) {
        handleDisplayAd();
      }
    }
  }, [slot, handleDisplayAd, adSlot]);

  useEffect(() => {
    /**
     * This function destroys the ad slot when the component is unmounted.
     * @returns void
     * @see https://developers.google.com/doubleclick-gpt/reference#googletag.destroySlots
     */
    return () => {
      const gTag = window.googletag as typeof googletag | undefined;
      if (gTag && gTag.apiReady && adSlot) {
        googletag.cmd.push(function () {
          googletag.destroySlots([adSlot]);
        });
      }
    };
  }, [adSlot]);

  return (
    <div className={rootClassName}>
      {!error ? <div data-testid={id} id={id}></div> : error}
    </div>
  );
};
